import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import {
  getPreferencesAuthenticated,
  getPreferencesByToken,
  updatePreferenceAuthenticated,
  updatePreferenceByToken,
} from '@/services/emailPreferenceService';
import { useAuthStore } from '@/store/authStore';

import type { EmailCategory } from '@/types/emailPreference.types';

type PageState = 'loading' | 'error' | 'ready';
type Channel = 'email' | 'sms';

interface ChannelCategoryState {
  email: EmailCategory[];
  sms: EmailCategory[];
}

export function EmailPreferencesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [categories, setCategories] = useState<ChannelCategoryState>({ email: [], sms: [] });
  const [originalCategories, setOriginalCategories] = useState<ChannelCategoryState>({ email: [], sms: [] });
  const [isSaving, setIsSaving] = useState(false);

  const isTokenMode = !!token;
  const hasAccess = isTokenMode || isAuthenticated;
  // Token-based unsubscribe is email-only; the authenticated flow shows both columns.
  const showSmsColumn = !isTokenMode;

  const loadPreferences = useCallback(async () => {
    try {
      setPageState('loading');
      const response = isTokenMode ? await getPreferencesByToken(token) : await getPreferencesAuthenticated();

      const next: ChannelCategoryState = {
        email: response.data.categories,
        sms: response.data.smsCategories ?? [],
      };
      setCategories(next);
      setOriginalCategories({ email: [...next.email], sms: [...next.sms] });
      setPageState('ready');
    } catch {
      setPageState('error');
    }
  }, [isTokenMode, token]);

  useEffect(() => {
    if (!hasHydrated) return;

    if (!hasAccess) {
      void navigate('/auth', { state: { from: { pathname: '/email-preferences' } }, replace: true });
      return;
    }

    void loadPreferences();
  }, [hasHydrated, hasAccess, loadPreferences, navigate]);

  const handleToggle = (channel: Channel, category: string) => {
    setCategories((prev) => ({
      ...prev,
      [channel]: prev[channel].map((c) =>
        c.category === category && !c.mandatory ? { ...c, optedOut: !c.optedOut } : c,
      ),
    }));
  };

  const hasChannelChanges = (channel: Channel) =>
    categories[channel].some((c, i) => c.optedOut !== originalCategories[channel][i]?.optedOut);
  const hasChanges = hasChannelChanges('email') || hasChannelChanges('sms');

  const allOptionalOptedOut = (channel: Channel): boolean =>
    categories[channel].filter((c) => !c.mandatory).every((c) => c.optedOut);

  const handleResubscribeAll = (channel: Channel) => {
    setCategories((prev) => ({
      ...prev,
      [channel]: prev[channel].map((c) => (c.mandatory ? c : { ...c, optedOut: false })),
    }));
  };

  const handleUnsubscribeAll = (channel: Channel) => {
    setCategories((prev) => ({
      ...prev,
      [channel]: prev[channel].map((c) => (c.mandatory ? c : { ...c, optedOut: true })),
    }));
  };

  const handleSave = async () => {
    const changedEmail = categories.email.filter((c, i) => c.optedOut !== originalCategories.email[i]?.optedOut);
    const changedSms = categories.sms.filter((c, i) => c.optedOut !== originalCategories.sms[i]?.optedOut);

    if (changedEmail.length === 0 && changedSms.length === 0) {
      toast.info(t('emailPreferences.noChanges'));
      return;
    }

    setIsSaving(true);
    try {
      const tasks: Array<Promise<void>> = [];
      for (const c of changedEmail) {
        tasks.push(
          isTokenMode
            ? updatePreferenceByToken(token, c.category, c.optedOut)
            : updatePreferenceAuthenticated('email', c.category, c.optedOut),
        );
      }
      for (const c of changedSms) {
        tasks.push(updatePreferenceAuthenticated('sms', c.category, c.optedOut));
      }
      await Promise.all(tasks);
      setOriginalCategories({ email: [...categories.email], sms: [...categories.sms] });
      toast.success(t('emailPreferences.saved'));
    } catch {
      toast.error(t('emailPreferences.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasHydrated) {
    return (
      <div className='flex items-center justify-center min-h-[50vh]'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent' />
      </div>
    );
  }

  if (pageState === 'loading') {
    return (
      <div className='max-w-3xl mx-auto p-4 md:p-8'>
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4' />
          <p className='text-gray-500'>{t('emailPreferences.loading')}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className='max-w-3xl mx-auto p-4 md:p-8'>
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center'>
          <div className='w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4'>
            <svg className='w-6 h-6 text-red-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
          </div>
          <p className='text-gray-700 font-medium mb-2'>{t('emailPreferences.loadFailed')}</p>
          {isTokenMode && <p className='text-sm text-gray-500'>{t('emailPreferences.invalidToken')}</p>}
          <Link
            to='/'
            className='inline-block mt-6 text-sm font-medium text-primary hover:text-primary-dark transition'>
            {t('emailPreferences.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  // Source-of-truth for category metadata (label/description/mandatory) — both channels
  // render the same set, so we drive the rows from the email list.
  const rows = categories.email;
  const smsByCategory = new Map(categories.sms.map((c) => [c.category, c]));

  return (
    <div className='max-w-3xl mx-auto p-4 md:p-8'>
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
        {/* Header */}
        <div className='px-6 py-5 border-b border-gray-100'>
          <h1 className='text-xl font-semibold text-gray-900'>{t('emailPreferences.title')}</h1>
          <p className='mt-1 text-sm text-gray-500'>{t('emailPreferences.subtitle')}</p>
        </div>

        {/* Column header */}
        <div className='px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center'>
          <div className='flex-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>
            {t('emailPreferences.categoryColumn')}
          </div>
          <div className='w-20 text-xs font-semibold uppercase tracking-wide text-gray-500 text-center'>
            {t('emailPreferences.emailColumn')}
          </div>
          {showSmsColumn && (
            <div className='w-20 text-xs font-semibold uppercase tracking-wide text-gray-500 text-center'>
              {t('emailPreferences.smsColumn')}
            </div>
          )}
        </div>

        {/* Category rows */}
        <div className='divide-y divide-gray-100'>
          {rows.map((category) => {
            const smsRow = smsByCategory.get(category.category);
            return (
              <div key={category.category} className='px-6 py-4 flex items-center gap-4'>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium text-gray-900'>{category.label}</span>
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        category.mandatory ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700',
                      )}>
                      {category.mandatory ? t('emailPreferences.requiredBadge') : t('emailPreferences.optionalBadge')}
                    </span>
                  </div>
                  <p className='mt-0.5 text-sm text-gray-500'>{category.description}</p>
                </div>

                <ToggleSwitch
                  checked={!category.optedOut}
                  disabled={category.mandatory}
                  ariaLabel={`${category.label} ${t('emailPreferences.emailColumn')}`}
                  onClick={() => handleToggle('email', category.category)}
                />
                {showSmsColumn && smsRow && (
                  <ToggleSwitch
                    checked={!smsRow.optedOut}
                    disabled={smsRow.mandatory}
                    ariaLabel={`${category.label} ${t('emailPreferences.smsColumn')}`}
                    onClick={() => handleToggle('sms', category.category)}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className='px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3'>
          <div className='flex flex-wrap gap-x-4 gap-y-2 justify-center sm:justify-start'>
            <button
              type='button'
              onClick={() =>
                allOptionalOptedOut('email') ? handleResubscribeAll('email') : handleUnsubscribeAll('email')
              }
              className='text-sm font-medium text-gray-600 hover:text-gray-900 transition'>
              {allOptionalOptedOut('email')
                ? t('emailPreferences.resubscribeAllEmail')
                : t('emailPreferences.unsubscribeAllEmail')}
            </button>
            {showSmsColumn && (
              <button
                type='button'
                onClick={() => (allOptionalOptedOut('sms') ? handleResubscribeAll('sms') : handleUnsubscribeAll('sms'))}
                className='text-sm font-medium text-gray-600 hover:text-gray-900 transition'>
                {allOptionalOptedOut('sms')
                  ? t('emailPreferences.resubscribeAllSms')
                  : t('emailPreferences.unsubscribeAllSms')}
              </button>
            )}
          </div>

          <button
            type='button'
            onClick={() => void handleSave()}
            disabled={!hasChanges || isSaving}
            className={cn(
              'px-5 py-2 text-sm font-bold rounded transition',
              hasChanges && !isSaving
                ? 'bg-primary text-white hover:bg-primary-dark'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed',
            )}>
            {isSaving ? t('emailPreferences.saving') : t('emailPreferences.save')}
          </button>
        </div>
      </div>

      {/* Mandatory notice */}
      <p className='mt-4 text-xs text-gray-400 text-center'>{t('emailPreferences.mandatoryNote')}</p>
    </div>
  );
}

interface ToggleSwitchProps {
  checked: boolean;
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}

function ToggleSwitch({ checked, disabled, ariaLabel, onClick }: ToggleSwitchProps) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        disabled
          ? 'bg-gray-200 cursor-not-allowed opacity-60'
          : checked
            ? 'bg-primary cursor-pointer'
            : 'bg-gray-200 cursor-pointer',
      )}>
      <span
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
}
